import type { MixedDressingDetails } from '@sg/graphql-schema';

import { type Modification, type Product } from '../../types';
import {
  calculateMixedDressingCalories,
  checkIfDressingIngredient,
  checkIfNonDressingIngredient,
} from '../dressings';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calculates the total calories of a product based on its active modifications.
 *
 * NOTE: If the product cannot be modified (for example, sides, drinks),
 *       the standard calories value will be used.
 */
export function calculateActiveCalories(params: Params) {
  const { product, activeModifications, mixedDressingDetails } = params;

  const { isModifiable, calories } = product;

  // if the product cannot be modified, return the original calorie value
  if (!isModifiable) {
    return calories;
  }

  const calculateDressingCalories = createGetDressingIngredientCalories(mixedDressingDetails); // prettier-ignore
  const dressingModifications = activeModifications.filter(checkIfDressingIngredient); // prettier-ignore
  const dressingModificationsCalories = dressingModifications.reduce(calculateDressingCalories, 0); // prettier-ignore

  const otherModifications = activeModifications.filter(checkIfNonDressingIngredient); // prettier-ignore
  const otherModificationsCalories = otherModifications.reduce(getModificationIngredientCalories, 0); // prettier-ignore

  return dressingModificationsCalories + otherModificationsCalories;
}

// ─── Utils ───────────────────────────────────────────────────────────────────

function createGetDressingIngredientCalories(
  mixedDressingDetails: MixedDressingDetails[],
) {
  return (total: number, modification: Modification) =>
    total + calculateMixedDressingCalories(modification, mixedDressingDetails);
}

function getModificationIngredientCalories(
  total: number,
  modification: Modification,
) {
  return total + calculateModificationCalories(modification);
}

function calculateModificationCalories(modification: Modification) {
  const { ingredient, quantity } = modification;
  const ingredientCalories = ingredient?.calories ?? 0;

  return ingredientCalories * quantity;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  product: Product;
  activeModifications: Modification[];
  mixedDressingDetails: MixedDressingDetails[];
};
